import React, { useEffect, useRef, useState } from 'react';
import WorkOrderEmployeeGrid from './work-order-employee-grid';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { useLoadData } from '../../../hooks/useLoadData';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { Box, Button, Dialog, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import * as yup from 'yup';
import { useSaveData } from '../../../hooks/useSaveData';
import CloseIcon from '@mui/icons-material/Close';
import { Padding } from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

interface TimeAndMaterial {
  workOrderTimeAndMaterialDetailsID: number;
  workOrderID: number;
  workedHours: number;
  workedOn: string;
  employeeName: string;
  invoiceNumber: string | null;
  invoiceAmount: string;
  invoiceDate: string | null;
  invoiceNo: string | null;
  invoiceDueDate: string | null;
  temPId?: string; // Optional property for temporary ID
}

const timeAndMeterialValidationSchema = yup.object().shape({
  wkEmployee: yup.array().of(
    yup.object().shape({
      employeeName: yup.string().required('Employee Name is required'),
      workedOn: yup.string().required('Worked On is required').typeError('Worked On must be a valid date'),
      workedHours: yup
        .number()
        .required('Worked Hours is required')
        .typeError('Worked Hours must be a number')
        .positive('Worked Hours must be greater than zero'),
    })
  ),
});

interface IWorkOrderEmployee {
  isOpen: boolean;
  handleCloseEmployeeGrid: () => void;
  workOrderId: number;
}

const WorkOrderEmployee = ({ isOpen, handleCloseEmployeeGrid, workOrderId }: IWorkOrderEmployee) => {
  const form = useReactHookForm({
    defaultValues: {},
    yupSchema: timeAndMeterialValidationSchema,
  });

  const { isLoading, data, refetch, isFetching } = useLoadData<ApiResponse<[]>>(
    workOrdersApiKeyStore.getTimeandMaterialDetails(workOrderId)
  );

  const ref = useRef(false);

  const { isLoading: isStatusLoading, mutateAsync } = useSaveData(workOrdersApiKeyStore.updateTimeAndMeterial());

  const [updatedData, setUpdatedData] = useState<TimeAndMaterial[]>([]);

  useEffect(() => {
    if (data?.data) {
      // Ensure data is not undefined and is of the correct type
      const idAddedData = data.data?.map((timeAndMaterial: TimeAndMaterial, i: number) => {
        // Ensure we're spreading a valid object here
        return {
          ...timeAndMaterial, // Properly spreading the object
          temPId: `o-${i + 1}`, // Add temporary ID
        };
      });
      setUpdatedData(idAddedData); // Update state with the new data
    }
  }, [data]);

  const handleRefetchCall = () => {
    refetch();
  };

  const handleSaveButtonClick = () => {
    ref.current = true; // Mark Save button clicked
  };
  const handleSubmit = async (data: any) => {
    if (ref.current)
      try {
        const formattedData = {
          workOrderID: workOrderId,
          timeandMaterialDetails: data?.wkEmployee?.map((item: any) => ({
            ...item,
            workedOn: format(new Date(item.workedOn), 'MM/dd/yyyy'), // Format the date
          })),
        };
        await mutateAsync({
          data: formattedData,
        });
        handleRefetchCall();
      } catch (error) {
        // console.error('API call failed', error);
      } finally {
        ref.current = false; // Reset ref after API call
      }
  };

  const handleClose = () => {
    handleCloseEmployeeGrid();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={isOpen}
      fullWidth={true}
      maxWidth={'lg'}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 10,
          top: 3,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <div style={{ padding: 40 }}>
        <ReactHookForm
          formName="WorkOrderEmployee"
          onSubmit={handleSubmit}
          formContextProps={form}
          isBusy={isStatusLoading || isLoading || isFetching}
        >
          <WorkOrderEmployeeGrid data={updatedData} refetch={handleRefetchCall} name={'wkEmployee'} />
          <Grid container justifyContent="flex-end" spacing={2} style={{ marginTop: 20 }}>
            <Grid item>
              <Button
                type="submit"
                onClick={handleSaveButtonClick}
                variant="contained"
                color="primary"
                disabled={isStatusLoading || isLoading || isFetching || isReadOnlyPermission}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </ReactHookForm>
      </div>
    </Dialog>
  );
};

export default WorkOrderEmployee;
